import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { toast } from 'sonner';
import { Lock, CirclePlus } from 'lucide-react';
import { formatDate } from '../../utils/dateFormatter';
import { canWrite } from '../../utils/permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPLOYEE_CATEGORIES = [
  'All',
  'Administrative',
  'Woven Line',
  'Knits Line',
  'Cutting Line',
  'Finishing Line'
];

const getCategoryTranslationKey = (category) => {
  const keyMap = {
    'All': 'all',
    'Administrative': 'administrative',
    'Woven Line': 'wovenLine',
    'Knits Line': 'knitsLine',
    'Cutting Line': 'cuttingLine',
    'Finishing Line': 'finishingLine'
  };
  return keyMap[category] || category;
};

export const Payroll = ({ forcedCategory = null }) => {
  const { getAuthHeader, user } = useAuth();
  const { t, language } = useLanguage();
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(forcedCategory || 'All');
  const [snapshots, setSnapshots] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('current');
  const [isCloseMonthOpen, setIsCloseMonthOpen] = useState(false);
  const prevMonthDate = new Date(); prevMonthDate.setDate(1); prevMonthDate.setMonth(prevMonthDate.getMonth() - 1);
  const [closeMonth, setCloseMonth] = useState(String(prevMonthDate.getMonth() + 1).padStart(2, '0'));
  const [closeYear, setCloseYear] = useState(String(prevMonthDate.getFullYear()));
  const [withdrawalEmployee, setWithdrawalEmployee] = useState(null);
  const [viewEmployee, setViewEmployee] = useState(null);
  const [employeeWithdrawals, setEmployeeWithdrawals] = useState([]);
  const [employeeLoans, setEmployeeLoans] = useState([]);
  const [loanMap, setLoanMap] = useState({});
  const [withdrawalForm, setWithdrawalForm] = useState({ amount: '', notes: '' });
  const [isWithdrawalSubmitting, setIsWithdrawalSubmitting] = useState(false);
  const [isClosingMonth, setIsClosingMonth] = useState(false);
  const [blurSalary, setBlurSalary] = useState(() => {
    const saved = localStorage.getItem('payrollBlurSalary');
    return saved === 'true';
  });

  useEffect(() => {
    fetchSnapshots();
  }, []);

  useEffect(() => {
    if (selectedMonth === 'current') {
      fetchCurrentEmployees();
    } else {
      fetchSnapshotData(selectedMonth);
    }
  }, [selectedMonth]);

  const fetchSnapshots = async () => {
    try {
      const response = await axios.get(`${API}/payroll/snapshots`, { headers: getAuthHeader() });
      setSnapshots(response.data);
    } catch (error) {
      console.error('Failed to fetch snapshots');
    }
  };

  const fetchCurrentEmployees = async () => {
    try {
      const [empRes, loansRes] = await Promise.all([
        axios.get(`${API}/employees`, { headers: getAuthHeader() }),
        axios.get(`${API}/loans`, { headers: getAuthHeader() }),
      ]);
      setEmployees(empRes.data);
      filterEmployees(empRes.data, selectedCategory);
      const now = new Date();
      const currentYM = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      const map = {};
      (loansRes.data || []).forEach(l => {
        if (l.status === 'active' && l.start_month <= currentYM) {
          map[l.employee_number] = (map[l.employee_number] || 0) + l.monthly_installment;
        }
      });
      setLoanMap(map);
    } catch (error) {
      toast.error('Failed to fetch payroll data');
    }
  };

  const fetchSnapshotData = async (snapshotKey) => {
    try {
      const [year, month] = snapshotKey.split('-');
      const response = await axios.get(`${API}/payroll/snapshot/${year}/${month}`, { headers: getAuthHeader() });
      setEmployees(response.data.employees);
      filterEmployees(response.data.employees, selectedCategory);
    } catch (error) {
      toast.error('Failed to fetch snapshot data');
    }
  };

  const handleCloseMonth = async () => {
    if (isClosingMonth) return;
    setIsClosingMonth(true);
    try {
      await axios.post(`${API}/payroll/close-month`, { month: closeMonth, year: closeYear }, { headers: getAuthHeader() });
      toast.success('Month closed successfully! OT, bonuses, deductibles, and withdrawals have been reset.');
      setIsCloseMonthOpen(false);
      fetchSnapshots();
      fetchCurrentEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to close month');
    } finally {
      setIsClosingMonth(false);
    }
  };

  const filterEmployees = (empList, category) => {
    if (category === 'All') {
      setFilteredEmployees(empList);
    } else {
      setFilteredEmployees(empList.filter(emp => emp.category === category));
    }
  };

  useEffect(() => {
    filterEmployees(employees, selectedCategory);
  }, [selectedCategory, employees]);

  const handleBlurToggle = (checked) => {
    setBlurSalary(checked);
    localStorage.setItem('payrollBlurSalary', checked.toString());
  };

  const handleRecordWithdrawal = async (e) => {
    e.preventDefault();
    if (isWithdrawalSubmitting) return;
    setIsWithdrawalSubmitting(true);
    try {
      await axios.post(`${API}/withdrawals`, {
        employee_number: withdrawalEmployee.employee_number,
        employee_name: withdrawalEmployee.name,
        amount: parseFloat(withdrawalForm.amount),
        notes: withdrawalForm.notes || null
      }, { headers: getAuthHeader() });
      toast.success('Withdrawal recorded');
      setWithdrawalEmployee(null);
      setWithdrawalForm({ amount: '', notes: '' });
      fetchCurrentEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record withdrawal');
    } finally {
      setIsWithdrawalSubmitting(false);
    }
  };

  const handleViewWithdrawals = async (emp) => {
    try {
      const [wRes, lRes] = await Promise.all([
        axios.get(`${API}/withdrawals/employee/${emp.employee_number}`, { headers: getAuthHeader() }),
        axios.get(`${API}/loans/employee/${emp.employee_number}`, { headers: getAuthHeader() }),
      ]);
      const now = new Date();
      const currentYM = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      setEmployeeWithdrawals(wRes.data || []);
      setEmployeeLoans((lRes.data || []).filter(l => l.status === 'active' && l.start_month <= currentYM));
      setViewEmployee(emp);
    } catch {
      toast.error('Failed to fetch withdrawal history');
    }
  };

  const getMonthLabel = (snapshot) => {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[parseInt(snapshot.month) - 1]} ${snapshot.year}`;
  };

  return (
    <Card className="p-6 border border-border">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <h2 className="text-2xl font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('payroll')}</h2>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm min-w-[150px]"
            data-testid="month-selector"
          >
            <option value="current">{t('currentMonth')}</option>
            {snapshots.map((snapshot) => (
              <option key={`${snapshot.year}-${snapshot.month}`} value={`${snapshot.year}-${snapshot.month}`}>
                {getMonthLabel(snapshot)}
              </option>
            ))}
          </select>
          {forcedCategory ? (
            <span className="text-sm font-medium px-3 py-2">{t(getCategoryTranslationKey(forcedCategory))}</span>
          ) : (
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
              data-testid="payroll-category-filter"
            >
              {EMPLOYEE_CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>{t(getCategoryTranslationKey(cat))}</option>
              ))}
            </select>
          )}
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={blurSalary}
              onChange={(e) => handleBlurToggle(e.target.checked)}
              className="w-4 h-4"
              data-testid="blur-salary-toggle"
            />
            <span className="text-sm" lang={language === 'ar' ? 'ar' : 'en'}>{t('blurSalaryNetPay')}</span>
          </label>
          {user?.role === 'admin' && selectedMonth === 'current' && (
            <Dialog open={isCloseMonthOpen} onOpenChange={setIsCloseMonthOpen}>
              <DialogTrigger asChild>
                <Button className="gap-2 bg-destructive hover:bg-destructive/90" data-testid="close-month-btn">
                  <Lock className="w-4 h-4" />
                  <span>{t('closeMonth')}</span>
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t('closeCurrentMonth')}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <Label>Month</Label>
                      <select
                        value={closeMonth}
                        onChange={e => setCloseMonth(e.target.value)}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      >
                        {['01','02','03','04','05','06','07','08','09','10','11','12'].map(m => (
                          <option key={m} value={m}>{m}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex-1">
                      <Label>Year</Label>
                      <select
                        value={closeYear}
                        onChange={e => setCloseYear(e.target.value)}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      >
                        {['2024','2025','2026','2027'].map(y => (
                          <option key={y} value={y}>{y}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <p className="text-sm font-semibold text-destructive">
                    {t('closeMonthConfirm')}
                  </p>
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" onClick={() => setIsCloseMonthOpen(false)}>
                      {t('cancel')}
                    </Button>
                    <Button className="bg-destructive hover:bg-destructive/90" onClick={handleCloseMonth} disabled={isClosingMonth}>
                      {t('yesCloseMonth')}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {selectedMonth !== 'current' && (
        <div className="mb-4 p-3 bg-muted/50 rounded-md border border-border">
          <p className="text-sm text-muted-foreground">
            📊 {t('viewingArchived')} <span className="font-semibold">{getMonthLabel(snapshots.find(s => `${s.year}-${s.month}` === selectedMonth) || {month: selectedMonth.split('-')[1], year: selectedMonth.split('-')[0]})}</span>
          </p>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead className="bg-muted/50">
            <tr>
              <th className="border border-border p-3 text-start font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('number')}</th>
              <th className="border border-border p-3 text-start font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('name')}</th>
              <th className="border border-border p-3 text-end font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('salary')}</th>
              <th className="border border-border p-3 text-end font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('ss')}</th>
              <th className="border border-border p-3 text-end font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('ot')}</th>
              <th className="border border-border p-3 text-end font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('bonuses')}</th>
              <th className="border border-border p-3 text-end font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('deductibles')}</th>
              <th className="border border-border p-3 text-end font-semibold bg-accent/10" lang={language === 'ar' ? 'ar' : 'en'}>{t('netPay')}</th>
              <th className="border border-border p-3 text-end font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('withdrawals')}</th>
              <th className="border border-border p-3 text-end font-semibold bg-success/10" lang={language === 'ar' ? 'ar' : 'en'}>{t('balanceRemaining')}</th>
              {selectedMonth === 'current' && canWrite(user?.role) && <th className="border border-border p-3 w-10" />}
            </tr>
          </thead>
          <tbody>
            {filteredEmployees.map((emp, idx) => {
              const loanInstallment = selectedMonth === 'current' ? (loanMap[emp.employee_number] || 0) : 0;
              const netPay = emp.salary - emp.ss + emp.ot + emp.bonuses - emp.deductibles;
              const totalWithdrawals = (emp.withdrawals || 0) + loanInstallment;
              const balanceRemaining = netPay - totalWithdrawals;
              return (
                <tr key={idx} className="hover:bg-muted/30" data-testid={`payroll-row-${idx}`}>
                  <td className="border border-border p-3 font-mono-numeric">{emp.employee_number}</td>
                  <td className={`border border-border p-3 ${language === 'ar' ? 'text-base font-bold' : ''}`}>
                    <span
                      className="cursor-pointer hover:underline text-accent"
                      onClick={() => handleViewWithdrawals(emp)}
                    >
                      {emp.name}
                    </span>
                  </td>
                  <td className={`border border-border p-3 text-end font-mono-numeric ${blurSalary ? 'blur-sm select-none' : ''}`}>{emp.salary.toFixed(2)}</td>
                  <td className="border border-border p-3 text-end font-mono-numeric text-red-600">-{emp.ss.toFixed(2)}</td>
                  <td className="border border-border p-3 text-end font-mono-numeric text-green-600">+{emp.ot.toFixed(2)}</td>
                  <td className="border border-border p-3 text-end font-mono-numeric text-green-600">+{emp.bonuses.toFixed(2)}</td>
                  <td className="border border-border p-3 text-end font-mono-numeric text-red-600">-{emp.deductibles.toFixed(2)}</td>
                  <td className={`border border-border p-3 text-end font-mono-numeric font-semibold bg-accent/10 ${blurSalary ? 'blur-sm select-none' : ''}`}>{netPay.toFixed(2)} {t('currency')}</td>
                  <td className="border border-border p-3 text-end font-mono-numeric text-red-600">-{totalWithdrawals.toFixed(2)}</td>
                  <td className="border border-border p-3 text-end font-mono-numeric font-bold bg-success/10">{balanceRemaining.toFixed(2)} {t('currency')}</td>
                  {selectedMonth === 'current' && canWrite(user?.role) && (
                    <td className="border border-border p-3 text-center">
                      <Button variant="ghost" size="sm" onClick={() => setWithdrawalEmployee(emp)} title="Record withdrawal">
                        <CirclePlus className="w-4 h-4 text-accent" />
                      </Button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
        {filteredEmployees.length === 0 && (
          <div className="text-center py-12 text-muted-foreground" data-testid="empty-payroll">
            <p lang={language === 'ar' ? 'ar' : 'en'}>{t('noPayrollData')}</p>
          </div>
        )}
      </div>

      {/* Record Withdrawal Dialog */}
      <Dialog open={!!withdrawalEmployee} onOpenChange={(open) => { if (!open) { setWithdrawalEmployee(null); setWithdrawalForm({ amount: '', notes: '' }); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('withdrawals')} — {withdrawalEmployee?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRecordWithdrawal} className="space-y-4">
            {withdrawalEmployee && (() => {
              const netPay = (withdrawalEmployee.salary || 0) - (withdrawalEmployee.ss || 0) + (withdrawalEmployee.ot || 0) + (withdrawalEmployee.bonuses || 0) - (withdrawalEmployee.deductibles || 0);
              const remaining = netPay - (withdrawalEmployee.withdrawals || 0);
              return (
                <p className="text-sm text-muted-foreground">
                  {t('balanceRemaining')}:{' '}
                  <span className={`font-mono-numeric font-semibold ${remaining < 0 ? 'text-destructive' : 'text-success'}`}>
                    {remaining.toFixed(2)} {t('currency')}
                  </span>
                </p>
              );
            })()}
            <div>
              <Label>{t('amount')} ({t('currency')})</Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                value={withdrawalForm.amount}
                onChange={e => setWithdrawalForm(p => ({ ...p, amount: e.target.value }))}
                required
                autoFocus
              />
            </div>
            <div>
              <Label>{t('notes')}</Label>
              <Input
                value={withdrawalForm.notes}
                onChange={e => setWithdrawalForm(p => ({ ...p, notes: e.target.value }))}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => { setWithdrawalEmployee(null); setWithdrawalForm({ amount: '', notes: '' }); }}>{t('cancel')}</Button>
              <Button type="submit" className="bg-accent hover:bg-accent/90" disabled={isWithdrawalSubmitting}>{t('save')}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Withdrawal History Dialog */}
      <Dialog open={!!viewEmployee} onOpenChange={(open) => { if (!open) { setViewEmployee(null); setEmployeeLoans([]); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('withdrawals')} — {viewEmployee?.name}</DialogTitle>
          </DialogHeader>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead className="bg-muted/50">
                <tr>
                  <th className="border border-border p-2 text-start font-semibold">{t('type')}</th>
                  <th className="border border-border p-2 text-end font-semibold">{t('amount')}</th>
                  <th className="border border-border p-2 text-start font-semibold">{t('notes')}</th>
                </tr>
              </thead>
              <tbody>
                {employeeWithdrawals.map((w, i) => (
                  <tr key={`w-${i}`} className="hover:bg-muted/30">
                    <td className="border border-border p-2">{formatDate(w.date)}</td>
                    <td className="border border-border p-2 text-end font-mono-numeric text-destructive" dir="ltr">{w.amount.toFixed(2)} {t('currency')}</td>
                    <td className="border border-border p-2 text-muted-foreground">{w.notes || '—'}</td>
                  </tr>
                ))}
                {employeeLoans.map((l, i) => (
                  <tr key={`l-${i}`} className="hover:bg-muted/30 bg-warning/5">
                    <td className="border border-border p-2">
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-warning/20 text-warning" lang={language === 'ar' ? 'ar' : 'en'}>
                        {t('loans')}
                      </span>
                    </td>
                    <td className="border border-border p-2 text-end font-mono-numeric text-destructive" dir="ltr">{l.monthly_installment.toFixed(2)} {t('currency')}</td>
                    <td className="border border-border p-2 text-muted-foreground" lang={language === 'ar' ? 'ar' : 'en'}>{t('monthlyInstallment')} — {l.start_month}</td>
                  </tr>
                ))}
              </tbody>
              {(employeeWithdrawals.length > 0 || employeeLoans.length > 0) && (
                <tfoot>
                  <tr className="border-t-2 border-border bg-muted/30 font-semibold">
                    <td className="p-2">{t('total')}</td>
                    <td className="p-2 text-end font-mono-numeric text-destructive" dir="ltr">
                      {(
                        employeeWithdrawals.reduce((s, w) => s + w.amount, 0) +
                        employeeLoans.reduce((s, l) => s + l.monthly_installment, 0)
                      ).toFixed(2)} {t('currency')}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
            {employeeWithdrawals.length === 0 && employeeLoans.length === 0 && (
              <p className="text-center py-6 text-muted-foreground">{t('noEntriesYet')}</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {filteredEmployees.length > 0 && (
        <div className="mt-6 grid grid-cols-2 gap-4">
          <div className="p-4 bg-accent/10 rounded-md">
            <div className="flex justify-between items-center">
              <span className="font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('total')} {t('netPay')}:</span>
              <span className="text-2xl font-bold font-mono-numeric">
                {filteredEmployees.reduce((sum, emp) => sum + (emp.salary - emp.ss + emp.ot + emp.bonuses - emp.deductibles), 0).toFixed(2)} {t('currency')}
              </span>
            </div>
          </div>
          <div className="p-4 bg-success/10 rounded-md">
            <div className="flex justify-between items-center">
              <span className="font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('total')} {t('balanceRemaining')}:</span>
              <span className="text-2xl font-bold font-mono-numeric">
                {filteredEmployees.reduce((sum, emp) => {
                  const loanInst = selectedMonth === 'current' ? (loanMap[emp.employee_number] || 0) : 0;
                  return sum + (emp.salary - emp.ss + emp.ot + emp.bonuses - emp.deductibles - (emp.withdrawals || 0) - loanInst);
                }, 0).toFixed(2)} {t('currency')}
              </span>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};